import { useEffect, useState } from 'react';

// Shared page-loading gate for the LoadingOverlay: returns true while the page
// should keep its loading modal up — i.e. until `loading` (data fetches) turns
// false AND the given images have loaded in the browser. Capped (default 8s)
// so a broken image URL never blocks the page. Reusable by any future page:
//   const showLoading = useAssetsReady(isFetching, [url1, url2, ...]);
export function useAssetsReady(
  loading: boolean,
  imageUrls: Array<string | null | undefined>,
  capMs: number = 8000
): boolean {
  const [ready, setReady] = useState(false);
  // Stable dependency key: the array is usually rebuilt every render
  const urlsKey = imageUrls.filter(Boolean).join('\n');

  useEffect(() => {
    if (loading || ready) return;
    let cancelled = false;
    const urls = urlsKey ? urlsKey.split('\n') : [];
    const preload = Promise.all(
      urls.map(
        src =>
          new Promise<void>(resolve => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          })
      )
    );
    const timeout = new Promise<void>(resolve => setTimeout(resolve, capMs));
    Promise.race([preload, timeout]).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loading, ready, urlsKey, capMs]);

  return loading || !ready;
}
